import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';

let googleAccessToken = null;

// OAuth credentials stay in memory; application data is stored in Supabase.
if (supabase) {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') googleAccessToken = null;
    if (session?.provider_token) {
      googleAccessToken = session.provider_token;
    }
  });
}

/**
 * Inicia o login OAuth com o Google solicitando os escopos de leitura/escrita no Google Calendar.
 */
export async function signInWithGoogleCalendar() {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase não está configurado.');
  }

  const redirectUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      scopes: 'https://www.googleapis.com/auth/calendar.events',
      redirectTo: redirectUrl,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent'
      }
    }
  });

  if (error) throw error;
  return data;
}

/**
 * Recupera o Access Token da sessão atual do Supabase.
 */
export async function getGoogleAccessToken() {
  if (!isSupabaseConfigured || !supabase) {
    return googleAccessToken;
  }
  
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.provider_token) {
    googleAccessToken = session.provider_token;
    return session.provider_token;
  }

  return googleAccessToken;
}

/**
 * Salva um token localmente caso retornado na URL/OAuth.
 */
export function setLocalGoogleAccessToken(token) {
  if (token) {
    googleAccessToken = token;
  }
}

/**
 * Busca compromissos do Google Calendar do usuário primário.
 */
export async function fetchGoogleEvents(accessToken, timeMin, timeMax) {
  if (!accessToken) return { events: [], isUnauthorized: true };

  let url = 'https://www.googleapis.com/calendar/v3/calendars/primary/events?singleEvents=true&orderBy=startTime&maxResults=2500';
  
  if (timeMin) {
    url += `&timeMin=${encodeURIComponent(timeMin)}`;
  } else {
    // Padrão: buscar a partir de 2 meses atrás se timeMin não for informado
    const dMin = new Date();
    dMin.setMonth(dMin.getMonth() - 2);
    url += `&timeMin=${encodeURIComponent(dMin.toISOString())}`;
  }

  if (timeMax) {
    url += `&timeMax=${encodeURIComponent(timeMax)}`;
  }

  try {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!res.ok) {
      if (res.status === 401) {
        console.warn('Token do Google Calendar expirado ou inválido.');
        googleAccessToken = null;
        return { events: [], isUnauthorized: true };
      }
      return { events: [], error: `Erro ${res.status}: ${res.statusText}` };
    }

    const data = await res.json();
    if (!data.items) return { events: [] };

    const mappedEvents = data.items.map(item => {
      const startDT = item.start?.dateTime || item.start?.date;
      const endDT = item.end?.dateTime || item.end?.date;
      
      let dateStr = '';
      let timeStr = 'Dia inteiro';

      if (startDT) {
        if (startDT.includes('T')) {
          const d = new Date(startDT);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          dateStr = `${y}-${m}-${day}`;

          const startTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
          let endTime = '';
          if (endDT && endDT.includes('T')) {
            const ed = new Date(endDT);
            endTime = String(ed.getHours()).padStart(2, '0') + ':' + String(ed.getMinutes()).padStart(2, '0');
          }
          timeStr = endTime ? `${startTime} - ${endTime}` : startTime;
        } else {
          // É uma data no formato "YYYY-MM-DD"
          dateStr = startDT;
        }
      }

      // Determinar período (Manhã / Tarde / Noite)
      let period = 'Manhã';
      if (timeStr.includes(':')) {
        const hour = parseInt(timeStr.split(':')[0], 10);
        if (hour >= 12 && hour < 18) period = 'Tarde';
        else if (hour >= 18) period = 'Noite';
      }

      const GOOGLE_COLOR_MAP = {
        '1': '#a4bdfc',
        '2': '#7ae7bf',
        '3': '#dbadff',
        '4': '#ff887c',
        '5': '#fbd75b',
        '6': '#ffb878',
        '7': '#46d6db',
        '8': '#e1e1e1',
        '9': '#5484ed',
        '10': '#51b749',
        '11': '#dc2127'
      };

      const isBirthday = isBirthdayEvent({
        title: item.summary,
        description: item.description
      });

      const eventColor = isBirthday 
        ? '#a855f7' 
        : (item.colorId ? GOOGLE_COLOR_MAP[item.colorId] : '#0284c7');

      return {
        id: item.id,
        title: item.summary || 'Sem Título',
        date: dateStr,
        time: timeStr,
        period,
        calendarColor: eventColor,
        color: '#10b981',
        difficulty: 'low',
        description: item.description || '',
        category: 'Pessoal',
        isBirthday,
        isCalendarEvent: true,
        htmlLink: item.htmlLink
      };
    });

    return { events: mappedEvents };
  } catch (err) {
    console.error('Erro ao buscar eventos do Google Calendar:', err);
    return { events: [], error: err.message };
  }
}

/**
 * Verifica se um evento ou texto refere-se a um aniversário.
 */
export function isBirthdayEvent(item) {
  if (!item) return false;
  const title = (item.title || item.summary || item.text || '').toLowerCase();
  const desc = (item.description || '').toLowerCase();
  const fullText = `${title} ${desc}`;
  
  const birthdayRegex = /\b(anivers[aá]rio|birthday|bday|b-day|niver|parab[eé]ns|cumplea[ñn]os)\b/i;
  const birthdayEmojis = ['🎂', '🎈', '🎉', '🍰', '🎁'];
  
  if (birthdayRegex.test(fullText)) return true;
  if (birthdayEmojis.some(emoji => fullText.includes(emoji))) return true;
  return false;
}

/**
 * Cria um novo compromisso na agenda do Google do usuário.
 */
export async function createGoogleEvent(accessToken, eventData) {
  if (!accessToken) throw new Error('Acesso do Google não autorizado.');

  const startDateStr = `${eventData.date || getTodayStr()}T09:00:00Z`;
  const endDateStr = `${eventData.date || getTodayStr()}T10:00:00Z`;

  const body = {
    summary: eventData.title,
    description: eventData.description || 'Criado via Daily Planner',
    start: { dateTime: startDateStr },
    end: { dateTime: endDateStr }
  };

  const res = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.error?.message || 'Falha ao criar evento no Google Calendar.');
  }

  return await res.json();
}

function getTodayStr() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Updates the selected event instance while preserving its time and duration. */
export async function updateGoogleEvent(accessToken, eventId, task) {
  if (!accessToken) throw new Error('Reconecte sua Google Agenda para salvar as alterações.');
  if (!eventId) throw new Error('Compromisso da Google Agenda não encontrado.');
  const url = `https://www.googleapis.com/calendar/v3/calendars/primary/events/${encodeURIComponent(eventId)}`;
  const headers = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
  const readResponse = async (response) => {
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        googleAccessToken = null;
        throw new Error('Reconecte sua Google Agenda para salvar as alterações.');
      }
      throw new Error(data.error?.message || 'Não foi possível atualizar o compromisso no Google Agenda.');
    }
    return data;
  };
  const original = await readResponse(await fetch(url, { headers }));
  const body = { summary: task.text, description: task.description || '' };
  if (task.date && original.start?.date) {
    const duration = new Date(`${original.end.date}T00:00:00Z`) - new Date(`${original.start.date}T00:00:00Z`);
    body.start = { date: task.date };
    body.end = { date: new Date(new Date(`${task.date}T00:00:00Z`).getTime() + duration).toISOString().slice(0, 10) };
  } else if (task.date && original.start?.dateTime) {
    const start = new Date(original.start.dateTime);
    const duration = new Date(original.end.dateTime) - start;
    const [year, month, day] = task.date.split('-').map(Number);
    start.setFullYear(year, month - 1, day);
    body.start = { ...original.start, dateTime: start.toISOString() };
    body.end = { ...original.end, dateTime: new Date(start.getTime() + duration).toISOString() };
  }
  return readResponse(await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(body) }));
}
